import './header.scss';
import { gsap } from 'gsap';

/**
 * Header component — fullscreen menu overlay with GSAP animations.
 * Scoped to [data-component="header"]; supports one instance per page.
 */
const MOBILE_BP = 992; // matches viewport-9 breakpoint

function initHeader() {
	const root = document.querySelector( '[data-component="header"]' );
	if ( ! root ) {
		return;
	}

	const bar       = root.querySelector( '.p-header__bar' );
	const toggle    = root.querySelector( '.p-header__toggle' );
	const overlay   = root.querySelector( '.p-header__overlay' );
	const navLinks  = root.querySelectorAll( '.p-header__navLink' );
	const cards     = root.querySelectorAll( '.p-header__card' );
	const cta       = root.querySelector( '.p-header__cta' );
	const stickyCta = document.querySelector( '[data-component="sticky-cta"]' );

	function getFirstTabCategory() {
		const firstTab = root.querySelector( '.p-header__navLink[data-has-cards="1"]' );
		return firstTab && firstTab.dataset.category ? firstTab.dataset.category : '';
	}

	function isMobile() {
		return window.innerWidth < MOBILE_BP;
	}

	let isMenuOpen      = false;
	let activeCategory  = getFirstTabCategory();
	let menuTl          = null;

	// ─── Helpers ──────────────────────────────────────────────

	function showCardsForCategory( category ) {
		cards.forEach( ( card ) => {
			if ( card.dataset.category === category ) {
				card.classList.add( '-visible' );
			} else {
				card.classList.remove( '-visible' );
			}
		} );
	}

	function setActiveNav( category ) {
		navLinks.forEach( ( link ) => {
			if ( link.dataset.hasCards !== '1' ) {
				return;
			}
			if ( link.dataset.category === category ) {
				link.classList.add( '-active' );
			} else {
				link.classList.remove( '-active' );
			}
		} );
	}

	function getVisibleCards() {
		return root.querySelectorAll( '.p-header__card.-visible' );
	}

	// ─── Card entrance animation (after category switch) ─────

	function animateCardsIn() {
		const visible = getVisibleCards();
		if ( ! visible.length ) {
			return;
		}

		gsap.set( visible, { opacity: 0, yPercent: 30 } );

		visible.forEach( ( card ) => {
			gsap.set( card.querySelector( '.p-header__cardImgWrap' ), { width: 0 } );
			gsap.set( card.querySelector( '.p-header__cardDesc' ), { opacity: 0, y: 10 } );
			gsap.set( card.querySelector( '.p-header__cardImg' ), { opacity: 0 } );
		} );

		gsap.to( visible, {
			yPercent: 0,
			opacity: 1,
			duration: 0.5,
			stagger: 0.05,
			ease: 'power2.out',
			clearProps: 'yPercent,opacity',
		} );
	}

	// ─── Category switch ─────────────────────────────────────

	function setActiveCategory( category ) {
		if ( category === activeCategory ) {
			return;
		}
		activeCategory = category;
		setActiveNav( category );
		showCardsForCategory( category );
		animateCardsIn();
	}

	// ─── Hover animation on service cards ────────────────────

	function handleCardEnter( e ) {
		const el             = e.currentTarget;
		const contentWrapper = el.querySelector( '.p-header__cardContent' );
		const imageWrapper   = el.querySelector( '.p-header__cardImgWrap' );
		const hoverImage     = el.querySelector( '.p-header__cardImg' );
		const desc           = el.querySelector( '.p-header__cardDesc' );
		const arrow          = el.querySelector( '.p-header__cardArrow' );

		gsap.to( contentWrapper, { backgroundColor: 'rgba(137, 186, 231, 0.5)', duration: 0.3, ease: 'power2.out' } );
		gsap.to( imageWrapper, { width: '24%', marginLeft: '20px', duration: 0.4, ease: 'power2.out' } );
		gsap.to( hoverImage, { opacity: 1, duration: 0.3, delay: 0.1 } );
		gsap.to( desc, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' } );
		gsap.to( arrow, { rotation: -45, duration: 0.3, ease: 'power2.out' } );
	}

	function handleCardLeave( e ) {
		const el             = e.currentTarget;
		const contentWrapper = el.querySelector( '.p-header__cardContent' );
		const imageWrapper   = el.querySelector( '.p-header__cardImgWrap' );
		const hoverImage     = el.querySelector( '.p-header__cardImg' );
		const desc           = el.querySelector( '.p-header__cardDesc' );
		const arrow          = el.querySelector( '.p-header__cardArrow' );

		gsap.to( contentWrapper, { backgroundColor: '#1A3C4D', duration: 0.3, ease: 'power2.out' } );
		gsap.to( imageWrapper, { width: 0, marginLeft: 0, duration: 0.4, ease: 'power2.out' } );
		gsap.to( hoverImage, { opacity: 0, duration: 0.2 } );
		gsap.to( desc, { opacity: 0, y: 10, duration: 0.3, ease: 'power2.in' } );
		gsap.to( arrow, { rotation: 0, duration: 0.3, ease: 'power2.out' } );
	}

	// ─── Mobile sub-list accordion ───────────────────────────

	function collapseSubList( navItem, animated ) {
		const subList = navItem.querySelector( '.p-header__subList' );
		const link    = navItem.querySelector( '.p-header__navLink' );

		navItem.classList.remove( '-expanded' );
		if ( link ) {
			link.classList.remove( '-active' );
		}
		if ( subList ) {
			subList.setAttribute( 'aria-hidden', 'true' );
			if ( animated ) {
				gsap.to( subList, { height: 0, duration: 0.3, ease: 'power2.inOut' } );
			} else {
				gsap.set( subList, { height: 0 } );
			}
		}
	}

	function collapseAllSubLists( animated ) {
		root.querySelectorAll( '.p-header__navItem.-expanded' ).forEach( ( item ) => {
			collapseSubList( item, animated );
		} );
	}

	function toggleSubList( navItem ) {
		const isExpanded = navItem.classList.contains( '-expanded' );
		const subList    = navItem.querySelector( '.p-header__subList' );
		const link       = navItem.querySelector( '.p-header__navLink' );

		collapseAllSubLists( true );

		if ( ! isExpanded && subList ) {
			navItem.classList.add( '-expanded' );
			if ( link ) {
				link.classList.add( '-active' );
			}
			subList.setAttribute( 'aria-hidden', 'false' );
			const naturalHeight = subList.scrollHeight;
			gsap.fromTo(
				subList,
				{ height: 0 },
				{ height: naturalHeight, duration: 0.35, ease: 'power2.out' }
			);
		}
	}

	// ─── Menu open / close ───────────────────────────────────

	function openMenu() {
		isMenuOpen = true;
		root.classList.add( '-menuOpen' );
		bar.classList.add( '-menuOpen' );
		toggle.setAttribute( 'aria-expanded', 'true' );
		overlay.setAttribute( 'aria-hidden', 'false' );
		document.body.style.overflow = 'hidden';

		if ( stickyCta ) {
			stickyCta.classList.add( '-menuOpen' );
			gsap.killTweensOf( stickyCta );
			gsap.to( stickyCta, { y: 40, opacity: 0, duration: 0.35, ease: 'power2.in' } );
		}

		collapseAllSubLists( false );

		if ( isMobile() ) {
			navLinks.forEach( ( link ) => link.classList.remove( '-active' ) );
		}

		activeCategory = getFirstTabCategory();
		setActiveNav( activeCategory );
		showCardsForCategory( activeCategory );

		if ( menuTl ) {
			menuTl.kill();
		}

		menuTl = gsap.timeline();
		menuTl.set( overlay, { visibility: 'visible' } );
		menuTl.to( overlay, { y: '0%', duration: 0.8, ease: 'power4.inOut' } );

		const navItems   = root.querySelectorAll( '.p-header__navItem' );
		const visible    = getVisibleCards();
		const staggerEls = [ ...navItems, cta, ...visible ];

		gsap.set( staggerEls, { opacity: 0, yPercent: 30 } );

		visible.forEach( ( card ) => {
			gsap.set( card.querySelector( '.p-header__cardImgWrap' ), { width: 0 } );
			gsap.set( card.querySelector( '.p-header__cardDesc' ), { opacity: 0, y: 10 } );
			gsap.set( card.querySelector( '.p-header__cardImg' ), { opacity: 0 } );
		} );

		menuTl.to( staggerEls, {
			yPercent: 0,
			opacity: 1,
			duration: 0.5,
			stagger: 0.05,
			ease: 'power2.out',
			delay: -0.4,
			clearProps: 'transform',
		} );
	}

	function closeMenu() {
		isMenuOpen = false;
		root.classList.remove( '-menuOpen' );
		bar.classList.remove( '-menuOpen' );
		toggle.setAttribute( 'aria-expanded', 'false' );
		overlay.setAttribute( 'aria-hidden', 'true' );
		document.body.style.overflow = '';

		if ( stickyCta ) {
			stickyCta.classList.remove( '-menuOpen' );
			gsap.killTweensOf( stickyCta );
			gsap.to( stickyCta, { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.3 } );
		}

		collapseAllSubLists( false );

		if ( menuTl ) {
			menuTl.kill();
		}

		menuTl = gsap.timeline( {
			onComplete: () => {
				gsap.set( overlay, { visibility: 'hidden' } );
			},
		} );

		menuTl.to( overlay, { y: '-100%', duration: 0.6, ease: 'power3.inOut' } );
	}

	function toggleMenuState() {
		if ( isMenuOpen ) {
			closeMenu();
		} else {
			openMenu();
		}
	}

	// ─── Scroll detection ────────────────────────────────────

	function handleScroll() {
		if ( window.scrollY > 50 ) {
			bar.classList.add( '-scrolled' );
		} else {
			bar.classList.remove( '-scrolled' );
		}
	}

	// ─── Bind events ─────────────────────────────────────────

	toggle.addEventListener( 'click', toggleMenuState );

	navLinks.forEach( ( link ) => {
		link.addEventListener( 'click', ( e ) => {
			if ( link.dataset.hasCards !== '1' ) {
				closeMenu();
				return;
			}
			e.preventDefault();

			if ( isMobile() ) {
				const navItem = link.closest( '.p-header__navItem' );
				if ( navItem ) {
					toggleSubList( navItem );
				}
			} else {
				setActiveCategory( link.dataset.category );
			}
		} );
	} );

	// Close menu when clicking sub-links
	root.querySelectorAll( '.p-header__subLink' ).forEach( ( link ) => {
		link.addEventListener( 'click', () => {
			closeMenu();
		} );
	} );

	cards.forEach( ( card ) => {
		card.addEventListener( 'mouseenter', handleCardEnter );
		card.addEventListener( 'mouseleave', handleCardLeave );
		card.addEventListener( 'click', () => {
			closeMenu();
		} );
	} );

	root.querySelector( '.p-header__logo' ).addEventListener( 'click', () => {
		if ( isMenuOpen ) {
			closeMenu();
		}
	} );

	window.addEventListener( 'scroll', handleScroll, { passive: true } );

	// ─── Initial state ───────────────────────────────────────

	gsap.set( overlay, { y: '-100%', visibility: 'hidden' } );

	root.querySelectorAll( '.p-header__subList' ).forEach( ( list ) => {
		gsap.set( list, { height: 0 } );
	} );

	showCardsForCategory( activeCategory );
	handleScroll();
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHeader );
} else {
	initHeader();
}
